/**
 * Market Insights Panel
 * Displays daily market conditions with Over/Under analysis at leg and parlay level
 * Features a calendar view with clickable days showing detailed metrics
 */

import type { AnalysisLevel } from '@parlay-picker/shared/analyticsScopes';
import { formatShortDay } from '@parlay-picker/shared/formatting';
import { marketConditionLabel } from '@parlay-picker/shared/marketCondition';
import { deviceDay } from '@parlay-picker/shared/time';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import type { AdminMarketScope, DailyMarketInsight } from '../hooks/useLabDashboard';
import { LeagueConditionsList } from './LeagueConditionsList';
import { MarketCalendarLegend } from './MarketCalendarLegend';
import {
  getCallColor,
  getConditionBg,
  getConditionColor,
  getHitRateColor,
} from './marketInsightsHelpers';
import {
  AIInsightsSection,
  BiasAssessmentSection,
  PlayerPerformanceSection,
  PropTypeBreakdownSection,
  SituationalFactorsSection,
} from './marketInsightsSections';

// =============================================================================
// Calendar View
// =============================================================================

interface CalendarDayProps {
  date: Date;
  insight: DailyMarketInsight | null;
  isCurrentMonth: boolean;
  isSelected: boolean;
  level: AnalysisLevel;
  onClick: () => void;
}

function CalendarDay({
  date,
  insight,
  isCurrentMonth,
  isSelected,
  level,
  onClick,
}: CalendarDayProps): ReactElement {
  const dayNum = date.getDate();
  const hasData = insight !== null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!hasData}
      className={`
        relative p-1 min-h-[60px] rounded-lg text-xs transition-all border
        ${!isCurrentMonth ? 'opacity-30' : ''}
        ${hasData ? 'cursor-pointer hover:bg-white/5' : 'cursor-default'}
        ${isSelected ? 'ring-2 ring-blue-500' : ''}
        ${hasData ? getConditionBg(insight.marketCondition) : 'bg-theme-surface/20'}
        ${hasData ? 'border-theme-border/50' : 'border-transparent'}
      `}
    >
      <div className="absolute top-1 left-1.5 text-[10px] text-theme-text-secondary">{dayNum}</div>
      {hasData && (
        <div className="pt-4 flex flex-col items-center gap-0.5">
          <span className="text-lg">{insight.marketEmoji}</span>
          {level === 'legs' ? (
            <>
              <span className={`font-semibold ${getCallColor(insight.overall.call)}`}>
                {insight.legStats.hitRate}%
              </span>
              <span className="text-[10px] text-theme-text-tertiary">
                {insight.legStats.total} legs
              </span>
              {insight.legStats.pendingLegs > 0 && (
                <span className="text-[9px] text-theme-warning/70">
                  {insight.legStats.pendingLegs} pending
                </span>
              )}
            </>
          ) : (
            <>
              <span
                className={`font-semibold ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                {insight.parlayStats.hitRate}%
              </span>
              <span
                className={`text-[10px] ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
              >
                {insight.parlayStats.units >= 0 ? '+' : ''}
                {insight.parlayStats.units.toFixed(1)}u
              </span>
            </>
          )}
        </div>
      )}
    </button>
  );
}

interface CalendarViewProps {
  data: DailyMarketInsight[];
  selectedDate: string | null;
  level: AnalysisLevel;
  onSelectDate: (date: string) => void;
}

function CalendarView({
  data,
  selectedDate,
  level,
  onSelectDate,
}: CalendarViewProps): ReactElement {
  const [viewDate, setViewDate] = useState(() => {
    // Start with the most recent data date
    if (data.length > 0) {
      return new Date(`${data[0].date}T12:00:00`);
    }
    return new Date();
  });

  // Create a lookup map for insights by date
  const insightsByDate = useMemo(() => {
    const map = new Map<string, DailyMarketInsight>();
    for (const insight of data) {
      map.set(insight.date, insight);
    }
    return map;
  }, [data]);

  // Get calendar grid for the current month
  const calendarDays = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Start from Sunday before the first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on Saturday after the last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: Date[] = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  }, [viewDate]);

  const monthYear = viewDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
  const currentMonth = viewDate.getMonth();

  const prevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  return (
    <div className="bg-theme-surface/30 rounded-lg p-4">
      {/* Month Navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-theme-text-secondary" />
        </button>
        <h3 className="text-lg font-semibold text-white">{monthYear}</h3>
        <button
          type="button"
          onClick={nextMonth}
          className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-theme-text-secondary" />
        </button>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="text-center text-[10px] text-theme-text-tertiary uppercase py-1"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date) => {
          const dateStr = deviceDay(date);
          return (
            <CalendarDay
              key={dateStr}
              date={date}
              insight={insightsByDate.get(dateStr) || null}
              isCurrentMonth={date.getMonth() === currentMonth}
              isSelected={dateStr === selectedDate}
              level={level}
              onClick={() => onSelectDate(dateStr)}
            />
          );
        })}
      </div>

      <MarketCalendarLegend level={level} />
    </div>
  );
}

// =============================================================================
// Day Detail Modal
// =============================================================================

interface DayDetailModalProps {
  insight: DailyMarketInsight;
  level: AnalysisLevel;
  onClose: () => void;
}

function DayDetailModal({ insight, level, onClose }: DayDetailModalProps): ReactElement {
  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-theme-surface rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`p-4 border-b border-theme-border ${getConditionBg(insight.marketCondition)}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-3xl">{insight.marketEmoji}</span>
              <div>
                <h2 className="text-lg font-semibold text-white">{formatShortDay(insight.date)}</h2>
                <p className={`text-sm ${getConditionColor(insight.marketCondition)}`}>
                  {marketConditionLabel(insight.marketCondition)}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-theme-surface-hover/50 rounded transition-colors"
            >
              <X className="w-5 h-5 text-theme-text-secondary" />
            </button>
          </div>
          <p className="text-sm text-theme-text-secondary italic mt-2">"{insight.insight}"</p>
        </div>

        {/* Stats Grid */}
        <div className="p-4 space-y-4">
          <LeagueConditionsList leagues={insight.leagueConditions} />
          {/* Summary Row */}
          {level === 'legs' ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.overall.call)}`}>
                  {insight.legStats.hitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Hit Rate</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.over.call)}`}>
                  {insight.legStats.overHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Over Hit</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className={`text-2xl font-bold ${getCallColor(insight.under.call)}`}>
                  {insight.legStats.underHitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Under Hit</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">{insight.legStats.total}</div>
                <div className="text-xs text-theme-text-tertiary mt-1">Total Legs</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div
                  className={`text-2xl font-bold ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                >
                  {insight.parlayStats.hitRate}%
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Win Rate</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div
                  className={`text-2xl font-bold ${insight.parlayStats.units >= 0 ? 'text-theme-success' : 'text-theme-danger'}`}
                >
                  {insight.parlayStats.units >= 0 ? '+' : ''}
                  {insight.parlayStats.units.toFixed(2)}u
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Profit/Loss</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">
                  {insight.parlayStats.wins}W - {insight.parlayStats.losses}L
                </div>
                <div className="text-xs text-theme-text-tertiary mt-1">Record</div>
              </div>
              <div className="bg-theme-surface/50 rounded-lg p-3">
                <div className="text-2xl font-bold text-gray-200">{insight.parlayStats.total}</div>
                <div className="text-xs text-theme-text-tertiary mt-1">Total Parlays</div>
              </div>
            </div>
          )}

          {/* Leg-Level Performance — only when legs selected */}
          {level === 'legs' && (
            <div className="bg-theme-surface/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
                Leg-Level Performance
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-theme-text-tertiary text-xs">Record</div>
                  <div className="text-gray-200">
                    {insight.legStats.wins}W - {insight.legStats.total - insight.legStats.wins}L
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Over Hit</div>
                  <div className={getCallColor(insight.over.call)}>
                    {insight.legStats.overHitRate}% ({insight.legStats.overCount})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Under Hit</div>
                  <div className={getCallColor(insight.under.call)}>
                    {insight.legStats.underHitRate}% ({insight.legStats.underCount})
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Parlay-Level Performance — only when parlays selected */}
          {level === 'parlays' && (
            <div className="bg-theme-surface/30 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-theme-text-secondary uppercase mb-3">
                Parlay-Level Performance
              </h4>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-theme-text-tertiary text-xs">Record</div>
                  <div className="text-gray-200">
                    {insight.parlayStats.wins}W - {insight.parlayStats.losses}L
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">All-Over</div>
                  <div className={getHitRateColor(insight.parlayStats.allOverHitRate)}>
                    {insight.parlayStats.allOverHitRate}% ({insight.parlayStats.allOverParlays})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">All-Under</div>
                  <div className={getHitRateColor(insight.parlayStats.allUnderHitRate)}>
                    {insight.parlayStats.allUnderHitRate}% ({insight.parlayStats.allUnderParlays})
                  </div>
                </div>
                <div>
                  <div className="text-theme-text-tertiary text-xs">Mixed</div>
                  <div className={getHitRateColor(insight.parlayStats.mixedHitRate)}>
                    {insight.parlayStats.mixedHitRate}% ({insight.parlayStats.mixedParlays})
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Prop Type Breakdown */}
          <PropTypeBreakdownSection stats={insight.propTypeStats} />

          {/* Bias Assessment */}
          <BiasAssessmentSection bias={insight.biasAssessment} />

          {/* Situational Factors */}
          {insight.situationalStats && (
            <SituationalFactorsSection stats={insight.situationalStats} />
          )}

          {/* Player Performance */}
          {insight.playerStats &&
            (insight.playerStats.hotPlayers.length > 0 ||
              insight.playerStats.coldPlayers.length > 0) && (
              <PlayerPerformanceSection stats={insight.playerStats} />
            )}

          {/* AI Explanation */}
          {insight.ai && <AIInsightsSection ai={insight.ai} />}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

interface MarketInsightsPanelProps {
  data: DailyMarketInsight[];
  loading: boolean;
  error: string | null;
  generating: boolean;
  onGenerateInsights: () => Promise<number>;
  onRefresh: () => void;
  scope: AdminMarketScope;
  onScopeChange: (scope: AdminMarketScope) => void;
}

const SCOPE_OPTIONS: {
  value: AdminMarketScope;
  label: string;
  title: string;
}[] = [
  {
    value: 'everything',
    label: 'All Picks',
    title: 'AI picks + imported real bets + manual bets',
  },
  {
    value: 'real_bets',
    label: 'Real Bets',
    title: 'Only bets imported from sportsbooks',
  },
];

const LEVEL_OPTIONS: { value: AnalysisLevel; label: string }[] = [
  { value: 'legs', label: 'Legs' },
  { value: 'parlays', label: 'Parlays' },
];

export function MarketInsightsPanel({
  data,
  loading,
  error,
  generating,
  onGenerateInsights,
  onRefresh,
  scope,
  onScopeChange,
}: MarketInsightsPanelProps): ReactElement {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [level, setLevel] = useState<AnalysisLevel>('legs');

  // Find the selected insight
  const selectedInsight = selectedDate ? data.find((d) => d.date === selectedDate) : null;

  // Calculate summary stats based on level
  const { summaryStats, daysWithAI } = useMemo(() => {
    const aiDayCount = data.filter((d) => d.ai?.explanation).length;
    if (data.length === 0) return { summaryStats: null, daysWithAI: aiDayCount };
    if (level === 'legs') {
      const avgHitRate = (
        data.reduce((sum, d) => sum + d.legStats.hitRate, 0) / data.length
      ).toFixed(1);
      const avgOverHitRate = (
        data.reduce((sum, d) => sum + d.legStats.overHitRate, 0) / data.length
      ).toFixed(1);
      const totalLegs = data.reduce((sum, d) => sum + d.legStats.total, 0);
      return {
        summaryStats: {
          primary: `${avgHitRate}% avg hit`,
          secondary: `${avgOverHitRate}% over hit`,
          tertiary: `${totalLegs} legs`,
        },
        daysWithAI: aiDayCount,
      };
    }
    const totalUnits = data.reduce((sum, d) => sum + d.parlayStats.units, 0);
    const avgWinRate = (
      data.reduce((sum, d) => sum + d.parlayStats.hitRate, 0) / data.length
    ).toFixed(1);
    const totalParlays = data.reduce((sum, d) => sum + d.parlayStats.total, 0);
    return {
      summaryStats: {
        primary: `${avgWinRate}% win rate`,
        secondary: `${totalUnits >= 0 ? '+' : ''}${totalUnits.toFixed(1)}u`,
        tertiary: `${totalParlays} parlays`,
        unitsPositive: totalUnits >= 0,
      },
      daysWithAI: aiDayCount,
    };
  }, [data, level]);

  if (loading) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-8 text-center">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-theme-primary mx-auto mb-2" />
        <p className="text-theme-text-secondary text-sm">Loading market insights...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-theme-surface/50 rounded-lg p-4">
        <p className="text-theme-danger text-sm">{error}</p>
        <button
          type="button"
          onClick={onRefresh}
          className="mt-2 px-3 py-1 bg-theme-surface-hover text-theme-text-secondary rounded text-sm hover:bg-theme-surface-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-semibold text-white">Market Insights</h2>
          <p className="text-xs text-theme-text-tertiary mt-0.5">
            Click any day to see detailed metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Generate AI button */}
          {daysWithAI < data.length && (
            <button
              type="button"
              onClick={onGenerateInsights}
              disabled={generating}
              className="px-3 py-1.5 bg-theme-primary-fill text-theme-on-primary-fill rounded text-sm hover:bg-theme-primary-fill disabled:opacity-50 transition-colors"
            >
              {generating ? 'Generating...' : `Generate AI (${data.length - daysWithAI} days)`}
            </button>
          )}
        </div>
      </div>

      {/* Toggles row */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          {/* Scope toggle */}
          <div className="flex rounded-lg bg-theme-surface/50 p-0.5">
            {SCOPE_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => onScopeChange(opt.value)}
                title={opt.title}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  scope === opt.value
                    ? 'bg-theme-primary-fill text-theme-on-primary-fill'
                    : 'text-theme-text-secondary hover:text-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {/* Level toggle */}
          <div className="flex rounded-lg bg-theme-surface/50 p-0.5">
            {LEVEL_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                onClick={() => setLevel(opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  level === opt.value
                    ? 'bg-purple-600 text-white'
                    : 'text-theme-text-secondary hover:text-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {/* Summary stats */}
        {summaryStats && (
          <div className="flex items-center gap-4 text-sm">
            <span className="text-theme-text-secondary">{data.length} days</span>
            <span className={getHitRateColor(parseFloat(summaryStats.primary))}>
              {summaryStats.primary}
            </span>
            <span
              className={
                'unitsPositive' in summaryStats
                  ? summaryStats.unitsPositive
                    ? 'text-theme-success'
                    : 'text-theme-danger'
                  : getHitRateColor(parseFloat(summaryStats.secondary))
              }
            >
              {summaryStats.secondary}
            </span>
            <span className="text-theme-text-tertiary">{summaryStats.tertiary}</span>
          </div>
        )}
      </div>

      {/* Calendar View */}
      {data.length === 0 ? (
        <div className="bg-theme-surface/50 rounded-lg p-4">
          <p className="text-theme-text-secondary text-sm">
            No market insights available. Run settlement to generate data.
          </p>
        </div>
      ) : (
        <CalendarView
          data={data}
          selectedDate={selectedDate}
          level={level}
          onSelectDate={setSelectedDate}
        />
      )}

      {/* Day Detail Modal */}
      {selectedInsight && (
        <DayDetailModal
          insight={selectedInsight}
          level={level}
          onClose={() => setSelectedDate(null)}
        />
      )}
    </div>
  );
}
